// The Inbox: documents that arrived by email (worker/routes/inbox.ts). Types come from the Worker so they can't drift.
import type { ContractFields } from "../../shared/contract";
import type { InboxItem, ItemStatus } from "../../worker/inbox/items";
import { api } from "./api";

export type { InboxItem, ItemStatus };

export interface TrustedSender {
  email: string;
  addedAt: string;
}

export interface InboxAddress {
  enabled: boolean;
  address: string | null;
  createdAt?: string | null;
  /** Senders outside the workspace who get the reply and approve their own documents. */
  trusted?: TrustedSender[];
}

export interface ReadText {
  text: string;
  method: "pdf" | "ocr" | "docx";
}

/** Where an item's API lives: in the app, or (from an email's Edit link) the link's own session. */
export type ItemApi = { kind: "app"; id: string } | { kind: "link" };
export const itemBase = (where: ItemApi) => (where.kind === "app" ? `/api/inbox/${where.id}` : "/api/inbox-links/item");

export const getAddress = () => api<InboxAddress>("/api/inbox/address");
export const newAddress = () => api<InboxAddress>("/api/inbox/address", { method: "POST" });
export const trustSender = (email: string) => api<{ trusted: TrustedSender[] }>("/api/inbox/trusted", { method: "POST", json: { email } });
export const untrustSender = (email: string) => api<{ trusted: TrustedSender[] }>("/api/inbox/trusted", { method: "DELETE", json: { email } });
export const listInbox = (status: ItemStatus) => api<{ items: InboxItem[]; counts: Record<ItemStatus, number> }>(`/api/inbox?status=${status}`);
export const getItem = (where: ItemApi) => api<{ item: InboxItem; today?: string }>(itemBase(where));
export const acceptItem = (where: ItemApi, fields: ContractFields, text: ReadText | null) =>
  api<{ contractId: string }>(`${itemBase(where)}/accept`, { method: "POST", json: { fields, text } });
export const attachItem = (id: string, contractId: string, text: ReadText | null) =>
  api<{ contractId: string }>(`/api/inbox/${id}/attach`, { method: "POST", json: { contractId, text } });
export const discardItem = (where: ItemApi) => api<{ ok: true }>(`${itemBase(where)}/discard`, { method: "POST" });
/** Applies an update. From a scan the app read itself, it sends the details it found and the text. */
export const applyItem = (id: string, read?: { fields: ContractFields; text: ReadText }) =>
  api<{ contractId: string }>(`/api/inbox/${id}/apply`, { method: "POST", ...(read ? { json: read } : {}) });
export const restoreItem = (id: string) => api<{ ok: true }>(`/api/inbox/${id}/restore`, { method: "POST" });

/** The item's file, for reading a scan on this device. */
export async function fetchItemFile(where: ItemApi, item: Pick<InboxItem, "filename" | "contentType">): Promise<File> {
  const res = await fetch(`${itemBase(where)}/file`, { credentials: "same-origin" });
  if (!res.ok) throw new Error("Couldn't download the file.");
  return new File([await res.blob()], item.filename, { type: item.contentType });
}

export const fetchItemText = (where: ItemApi) => api<{ text: string }>(`${itemBase(where)}/text`).then((r) => r.text);

export interface LinkPeek {
  state: "ok" | "used" | "expired" | "unknown";
  purpose?: "create" | "edit" | "discard" | "apply" | "trust";
  itemId?: string;
  item?: {
    filename: string;
    /** Who sent it. */
    from?: string;
    status: ItemStatus;
    contractId: string | null;
    details: string[];
    /** Missing from servers older than thread updates. */
    kind?: "new" | "update";
    targetTitle?: string | null;
    /** "End date: 31 Dec 2026 → 31 Mar 2027", for an update or a newer draft. */
    changes?: string[];
  };
}
export const peekLink = (token: string) => api<LinkPeek>("/api/inbox-links/peek", { method: "POST", json: { token } });
export const redeemLink = (token: string) =>
  api<{ done: "created" | "discarded" | "edit" | "applied" | "trusted"; contractId?: string; itemId?: string; email?: string }>("/api/inbox-links/use", { method: "POST", json: { token } });

/** "from Asha (asha@example.com)" or "from asha@example.com". */
export const fromLine = (i: Pick<InboxItem, "from">) => (i.from.name ? `${i.from.name} (${i.from.email})` : i.from.email);
